import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { useApiMutation, useApiQuery } from "~/hooks/useApiQuery";
import { SnackbarContext } from "~/utils/Contexts";
import { ResponseError } from "~/utils/ResponseError";

import SmallButton from "./Button/SmallButton";

// The server answers a refused resend with 409 and one of these as a
// plain-text body.
const REFUSAL_REASONS = [
  "signed_in",
  "already_member",
  "inactive",
  "no_workos_user",
  "no_org",
  "not_found",
] as const;

type RefusalReason = (typeof REFUSAL_REASONS)[number];

const isRefusalReason = (value: string | undefined): value is RefusalReason =>
  (REFUSAL_REASONS as readonly (string | undefined)[]).includes(value);

const ResendInviteButton = ({
  basePath,
  body,
}: {
  basePath: "/user/administration" | "/user/management/users";
  body: { id: string; orgIdString?: string } | undefined;
}) => {
  const { t } = useTranslation("management");
  const snackbar = useContext(SnackbarContext);

  const status = useApiQuery<{ resendable: boolean }>({
    path: `${basePath}/GetInvitationStatus`,
    config: { method: "post", data: body },
    enabled: !!body,
    shouldShowErrorSnackbar: false,
  });

  const resend = useApiMutation<{ outcome: "sent" }>({
    path: `${basePath}/ResendInvitation`,
    config: { method: "post" },
    shouldShowErrorSnackbar: false,
    options: {
      onSuccess: () => {
        snackbar.open({ type: "success", text: t("users.resendInvite.sent") });
        void status.refetch();
      },
      onError: (error) => {
        const reason =
          error instanceof ResponseError && error.status === 409
            ? error.body?.trim()
            : undefined;
        snackbar.open({
          type: "error",
          text: isRefusalReason(reason)
            ? t(`users.resendInvite.refused.${reason}`)
            : t("users.resendInvite.failed"),
        });
        void status.refetch();
      },
    },
  });

  if (!body || !status.data?.resendable) return null;

  return (
    <SmallButton
      title={t("users.resendInvite.action")}
      variant="outlined"
      loading={resend.isPending}
      onClick={() => resend.mutate({ data: body })}
    />
  );
};

export default ResendInviteButton;
